import { useEffect, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { clsx } from 'clsx';
import { Icon, icon } from '@nula/core-ui';
import { NodeGhost } from '@/features/canvas/engine/surfaces/node-ghost';
import type { NodeType } from '@/features/canvas/utils/node-types';
import { EXPAND_GAP } from '@/features/canvas/utils/constants';
import styles from './selection-handles.module.css';

export type ExpandDirection = 'top' | 'right' | 'bottom' | 'left';

const POSITION_FOR: Record<ExpandDirection, Position> = {
  top: Position.Top,
  right: Position.Right,
  bottom: Position.Bottom,
  left: Position.Left,
};

const EXPAND_DOTS: { dir: ExpandDirection; label: string }[] = [
  { dir: 'top',    label: 'Expand top' },
  { dir: 'right',  label: 'Expand right' },
  { dir: 'bottom', label: 'Expand bottom' },
  { dir: 'left',   label: 'Expand left' },
];

export interface SelectionHandlesProps {
  nodeId: string;
  nodeType: NodeType;
  nodeWidth: number;
  nodeHeight: number;
  /** True while a node drag is in flight — hides the expand-dot chrome
   *  during drag so the user's drag isn't visually busy. */
  dragging: boolean;
  /** True when the node (or its surface, like a frame container) is
   *  locked. Locked nodes don't expose any handles. */
  locked: boolean;
  /** True when an edge is selected on the canvas — suppress all node
   *  hover chrome (drop handles, expand dots, ghost) so the user can
   *  drag the edge's reconnect anchors without nodes intercepting
   *  pointer events. Once a reconnect drag begins (isConnecting flips
   *  true) we re-enable, so the user has drop targets to land on. */
  suppressForEdgeSelection: boolean;
  /** True when the user is mid-connection-drag. We hide the expand
   *  ghost in this state so the connector preview is the only visual
   *  feedback. Drop handles stay visible. */
  isConnecting: boolean;
  /** True when the connector tool is the active canvas tool. Drives
   *  the body handle's pointer-events: a body-wide drag-to-connect
   *  affordance only when the user opted in. */
  connectorMode: boolean;
  /** Whether selection chrome (visible expand dots, ghost preview) is
   *  shown. Always-on drop handles stay mounted regardless so RF's
   *  handleBounds cache has them at first measurement. */
  showHandles: boolean;
  /** Click an expand dot to spawn a new node in that direction, with
   *  an edge connecting source → new. When absent, expand dots are
   *  not rendered at all (the 24px hit area would otherwise overlap a
   *  small node and block its onClick). */
  onExpand?: (nodeId: string, direction: ExpandDirection) => void;
  /** Content rendered inside the expand ghost preview (the floating
   *  "this is what you'll create" outline). Renders a plain box if
   *  omitted. */
  ghostContent?: React.ReactNode;
}

/**
 * Per-side handle chrome shown on a selected node — the body-wide
 * connector source plus the four-directional drop targets, expand
 * dots, hover bridge, and ghost preview.
 *
 * Frames are EXCLUDED from these affordances at the SelectionTransformer
 * dispatch layer (they're containers, not connection sources). This
 * component renders unconditionally; the parent gates on nodeType.
 *
 * Local state: hovered direction. Cleared automatically when
 * showHandles flips to false (e.g. selection cleared) so the "active"
 * scaled dot doesn't reappear next time the node gets selected
 * without the cursor actually being near it.
 */
export function SelectionHandles({
  nodeId,
  nodeWidth,
  nodeHeight,
  dragging,
  locked,
  suppressForEdgeSelection,
  isConnecting,
  connectorMode,
  showHandles,
  onExpand,
  ghostContent,
}: SelectionHandlesProps) {
  const [hoveredDir, setHoveredDir] = useState<ExpandDirection | null>(null);

  // Clear stale hover state when selection chrome disappears, so the
  // "active" scaled dot doesn't reappear next time the node gets
  // selected without the cursor actually being near it.
  useEffect(() => {
    if (!showHandles) setHoveredDir(null);
  }, [showHandles]);

  // No handles for locked nodes or while suppressing for an edge
  // selection. Returning null is fine — the parent renders this
  // alongside other selection chrome, and an empty render is invisible.
  if (locked || suppressForEdgeSelection) return null;

  return (
    <>
      {/* Full-body source Handle — only connectable when the Connector
          tool is active, so a drag from anywhere on the node body draws
          an edge. Outside connector mode, pointer-events: none keeps it
          out of the way of clicks/drags on the node itself. */}
      <Handle
        id="body"
        type="source"
        position={Position.Right}
        className={clsx(styles.bodyHandle, !connectorMode && styles.bodyHandleInactive)}
        isConnectable={connectorMode}
      />

      {/* Per-side drop handles + expand dots. */}
      {EXPAND_DOTS.map(({ dir, label }) => {
        const showDot = showHandles && onExpand && !dragging;
        return (
          <div
            key={dir}
            className={clsx(styles.expandWrap, styles[`expandWrap_${dir}`])}
            style={{ '--expand-gap': `${EXPAND_GAP}px` } as React.CSSProperties}
          >
            {/* Always-on invisible drop anchor — large drop target sized
                generously around the dot. FigJam-style: the visible dot is
                the small grab-handle; the larger transparent ring around
                it is what catches drops. Both Handles share the same
                centre (half-gap outside the node edge), so RF's path
                geometry is identical whether it routes to drop-{dir} or
                exp-{dir}.

                Stays isConnectable for RF's handle-bounds bookkeeping so
                connector drags resolve correctly, but pointer-events is
                CSS-gated on .dropHandleInert below — these 24×24 strips
                otherwise eat clicks at each side of the node. The class
                flips to inert outside an active connector drag. */}
            <Handle
              id={`drop-${dir}`}
              type="source"
              position={POSITION_FOR[dir]}
              className={clsx(
                styles.dropHandle,
                styles[`dropHandle_${dir}`],
                !isConnecting && styles.dropHandleInert,
              )}
              isConnectable
            />

            {/*
              * Single dual-purpose element: React Flow Handle styled as the
              * blue expand dot. Drag = start a connector (RF's connection
              * system catches pointerdown+move). Plain click = onExpand.
              *
              * Handle is ALWAYS mounted (not gated on showDot) so RF's
              * handleBounds cache is populated at first measurement. If we
              * mount/unmount based on selection, RF doesn't refresh the
              * cache when the handle appears, so connector drag from a
              * just-selected node fails until something forces a remeasure
              * (e.g. resizing). showDot only toggles visual chrome (opacity)
              * via the styles.expandDotHidden modifier below.
              *
              * Skipped entirely when onExpand is absent (e.g. text nodes) —
              * a hidden dot still has a 24px hit area and would overlap a
              * small node, blocking its onClick.
              */}
            {onExpand && (
              <Handle
                id={`exp-${dir}`}
                type="source"
                position={POSITION_FOR[dir]}
                role="button"
                aria-label={label}
                className={clsx(
                  styles.expandDot,
                  hoveredDir === dir && styles.expandDotActive,
                  !showDot && styles.expandDotHidden,
                  'nopan',
                )}
                onMouseEnter={showDot ? () => setHoveredDir(dir) : undefined}
                onMouseLeave={() => setHoveredDir(null)}
                onClick={showDot ? () => onExpand(nodeId, dir) : undefined}
                isConnectable={showDot}
              >
                <Icon name="add" size={icon.xs} className={styles.expandDotIcon} aria-hidden />
              </Handle>
            )}

            {showDot && hoveredDir === dir && !isConnecting && (
              <div
                className={styles.expandBridge}
                onMouseEnter={() => setHoveredDir(dir)}
                onMouseLeave={() => setHoveredDir(null)}
              />
            )}
            {showDot && hoveredDir === dir && !isConnecting && (
              <div
                className={styles.expandGhost}
                onMouseEnter={() => setHoveredDir(dir)}
                onMouseLeave={() => setHoveredDir(null)}
                onClick={() => onExpand!(nodeId, dir)}
                role="button"
                aria-label={`Duplicate ${dir}`}
              >
                <NodeGhost width={nodeWidth} height={nodeHeight}>{ghostContent}</NodeGhost>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
